import {
  ButtonGroup,
  Button,
  Typography,
  Box,
  // Switch,
  // Select,
  // MenuItem,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import './styles.css';
import dayjs from 'dayjs';
import { useAppSelector } from 'store/hook';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import { getDaysSinceStartOfYear, getStartAndEndDateFromRange } from '../utils';
import { useEffect, useMemo, useState } from 'react';
import { calculateDaysDifference } from 'views/FuelAnalytics/IdleDashboard/utils';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import useAppTheme from 'hooks/useAppTheme';
import ShiftwiseToggler from 'components/Layout/TopBar/components/ShiftwiseToggler';
// import ProductionSelector from 'views/Production/components/Selector/Selector';
// import { ShiftDetails } from 'views/Utilization/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  ignoreYtd?: boolean;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  aggregation: string;
  setAggregation: any;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  shiftNumber: number;
  setShiftNumber: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const FuelUsageDateRangeDiv = ({
  ignoreYtd = false,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  aggregation,
  setAggregation,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { loadingStates } = useAppSelector(
    (state) => state.fuelAnalyticsReducer
  );

  const disableComponent = useMemo(() => {
    return (
      loadingStates?.isLoadingFleetSummary ||
      loadingStates.isLoadingFuelConsumptionPerDayData ||
      loadingStates.isLoadingFuelTreemapData ||
      loadingStates.loadingFuelAnalyticsWRTDriverEvents
    );
  }, [loadingStates]);

  // const shiftOptions = ShiftDetails[customerCode] || ShiftDetails.others;
  const [aggregationOptions, setAggregationOptions] = useState([
    'Daily',
    'Monthly',
  ]);
  const handleSelect = (id: string) => {
    setIsSelected(id);

    // const today = dayjs();
    let newStartDate = startDate;
    let newEndDate = yesterday;

    switch (id) {
      case 'Today': {
        const startDateVal = new Date().toISOString().slice(0, 10);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(startDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '1D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(1);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '7D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(7);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '14D': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(14);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case '1M': {
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(30);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Daily');
        break;
      }
      case 'YTD': {
        const rangeInt = getDaysSinceStartOfYear();
        const { startDate: startDateVal, endDate: endDateVal } =
          getStartAndEndDateFromRange(rangeInt, true);
        newStartDate = dayjs(startDateVal).toDate();
        newEndDate = dayjs(endDateVal).toDate();
        setAggregation('Monthly');
        break;
      }
      case 'Custom':
        // Do nothing, user will select manually
        break;
      default:
        break;
    }

    // Update the startDate and endDate for non-custom ranges
    if (id !== 'Custom') {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  };

  let dateRangeList = ['Today', '1D', '7D', '14D', '1M', 'Custom'];
  if (ignoreYtd) {
    dateRangeList = dateRangeList.filter((item) => item !== 'YTD');
  }

  const dateRangeLabel = (item: string): string => {
    const map: Record<string, string> = {
      Today: t('date_range_today'),
      '1D': t('date_range_1d'),
      '7D': t('date_range_7d'),
      '14D': t('date_range_14d'),
      '1M': t('date_range_1m'),
      Custom: t('date_range_custom'),
    };
    return map[item] ?? item;
  };

  const aggregationLabel = (item: string): string => {
    const map: Record<string, string> = {
      Daily: t('aggregation_daily'),
      Monthly: t('aggregation_monthly'),
    };
    return map[item] ?? item;
  };

  const rangeIsOverMonth = ['Custom'].includes(isSelected);

  useEffect(() => {
    if (!startDate || !endDate) return;
    const days = calculateDaysDifference(startDate, endDate);
    if (days > 30) {
      setAggregationOptions(['Daily', 'Monthly']);
    } else {
      if (aggregation === 'Monthly') {
        setAggregation('Daily');
      }
      setAggregationOptions(['Daily']);
    }
  }, [rangeIsOverMonth, startDate, endDate]);

  return (
    <>
      <Typography
        style={{
          fontFamily: "'Inter Variable', sans-serif",
          margin: '20px 0',
          backgroundColor: enableDarkTheme
            ? palette.pageBackground
            : palette.pageBackground,
          padding: '5px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <div
            style={{
              color: enableDarkTheme
                ? ThemePalette.typography.mediumGray
                : '#3d3d3d',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Typography style={{ fontSize: '11px', marginRight: '5px' }}>
              {t('quick_range_10')}
            </Typography>
            <ButtonGroup
              size="small"
              variant="outlined"
              aria-label={t('basic_button_group')}
              sx={{
                border: `1px solid ${palette.primary}`,
                '& .MuiButton-root': {
                  borderColor: `${palette.primary} !important`,
                },
              }}
            >
              {dateRangeList.map((item) => {
                return (
                  <Button
                    key={item}
                    size="small"
                    disabled={disableComponent}
                    style={{
                      fontSize: ' 11px ',
                      backgroundColor:
                        isSelected === item
                          ? palette.primary
                          : enableDarkTheme
                          ? palette.boxBackground
                          : palette.toolBarBackground,
                      color: loadingStates?.isLoadingFleetSummary
                        ? ThemePalette.typography.grey
                        : isSelected === item
                        ? palette.white
                        : enableDarkTheme
                        ? palette.textPrimary
                        : palette.textPrimary,
                    }}
                    onClick={() => {
                      handleSelect(item);
                    }}
                  >
                    {dateRangeLabel(item)}
                  </Button>
                );
              })}
            </ButtonGroup>
            <ShiftwiseToggler
              disabled={disableComponent}
              shiftWiseAnalysis={shiftWiseAnalysis}
              setShiftWiseAnalysis={setShiftWiseAnalysis}
              shiftNumber={shiftNumber}
              setShiftNumber={setShiftNumber}
            />
          </div>

          {isSelected === 'Custom' && (
            <Box
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.mediumGray
                  : '#3d3d3d',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {aggregation === 'Monthly' ? (
                <CustomDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onFinalDateChange={handleDateChange}
                  maxDate={dayjs(yesterday)}
                  format="MMM YYYY"
                  views={['month', 'year']}
                  disabled={disableComponent || isSelected !== 'Custom'}
                  sx={{
                    minWidth: '148px',
                    maxWidth: '148px',
                    width: '148px',
                  }}
                  autoOpenEnabled={false}
                />
              ) : (
                <CustomDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onFinalDateChange={handleDateChange}
                  maxDate={dayjs(yesterday)}
                  sx={{
                    minWidth: '148px',
                    maxWidth: '148px',
                    width: '148px',
                  }}
                  disabled={disableComponent || isSelected !== 'Custom'}
                  autoOpenEnabled={false}
                />
              )}
              <Button
                onClick={() => {
                  handleFetchCustomDateRangeData({ aggregation });
                }}
                disabled={
                  disableComponent ||
                  isSelected !== 'Custom' ||
                  !startDate ||
                  !endDate
                }
                sx={{
                  fontSize: '11px',
                  backgroundColor: palette.primary,
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: '5px',
                  border: `1px solid ${palette.primary}`,
                  '&:hover': {
                    backgroundColor: palette.primaryDark,
                    color: '#fff',
                  },
                }}
              >
                {t('apply')}
              </Button>
            </Box>
          )}
          {rangeIsOverMonth && (
            <div
              style={{
                marginLeft: '10px',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography
                sx={{
                  fontSize: '11px',
                  color: enableDarkTheme
                    ? ThemePalette.typography.mediumGray
                    : '#3d3d3d',
                  marginTop: '7px',
                  marginRight: '10px',
                }}
              >
                {t('aggregation')}
              </Typography>

              <ProductionSelector
                options={aggregationOptions}
                selected={aggregation}
                setSelected={(value: string) => {
                  setAggregation(value);
                  if (isSelected === 'Custom') {
                    let sD = dayjs(startDate).toDate();
                    let eD = dayjs(endDate).toDate();
                    if (value === 'Monthly') {
                      sD = dayjs(startDate).startOf('month').toDate();
                      eD = dayjs(endDate).endOf('month').toDate();
                    }
                    handleFetchCustomOnAggregationChange({
                      aggregation: value,
                      startDate: sD,
                      endDate: eD,
                    });
                  }
                }}
                disabled={disableComponent}
                minWidth="100px"
                getOptionLabel={aggregationLabel}
              />
            </div>
          )}
        </div>
      </Typography>
    </>
  );
};

export default FuelUsageDateRangeDiv;
